import { useEffect, useState } from 'react';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { parseInvokeError, formatRateLimitMessage } from '@/utils/parseFunctionInvokeError';

export const AuthCallbackMicrosoft = () => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing authentication...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Parse both hash (implicit flow) and search params (code flow)
        const hash = window.location.hash.substring(1);
        const hashParams = new URLSearchParams(hash);
        const searchParams = new URLSearchParams(window.location.search);
        
        const accessToken = hashParams.get('access_token');
        const authCode = searchParams.get('code');
        const state = searchParams.get('state');
        const error = hashParams.get('error') || searchParams.get('error');
        const errorDescription = hashParams.get('error_description') || searchParams.get('error_description');

        // Handle errors
        if (error) {
          const errorMsg = errorDescription || error;
          
          window.opener?.postMessage({
            status: 'error',
            error: errorMsg,
            source: 'microsoft-graph-auth'
          }, window.location.origin);
          
          setStatus('error');
          setMessage(`Authentication failed: ${errorMsg}`);
          setTimeout(() => window.close(), 2000);
          return;
        }

        // Direct Login PKCE Flow: Code with or without dialog session
        if (authCode) {
          setMessage('Exchanging authorization code...');
          
          setMessage('Exchanging authorization code...');
          
          // Load PKCE code verifier with fallback chain (WebView2/Safari may lose sessionStorage)
          let codeVerifier = sessionStorage.getItem('pkce_verifier_direct_login');
          
          if (!codeVerifier) {
            codeVerifier = localStorage.getItem('pkce_verifier_direct_login');
          }
          
          if (!codeVerifier) {
            throw new Error('PKCE verification failed - verifier not found in sessionStorage or localStorage');
          }
          
          const { data: tokenData, error: exchangeError } = await supabase.functions.invoke('exchange-token', {
            body: {
              code: authCode,
              redirect_uri: `${window.location.origin}/auth-callback-microsoft`,
              flow: 'direct',
              code_verifier: codeVerifier
            }
          });

          // Clean up verifier from BOTH storages after use
          sessionStorage.removeItem('pkce_verifier_direct_login');
          localStorage.removeItem('pkce_verifier_direct_login');

          if (exchangeError || !tokenData?.accessToken) {
            throw new Error(exchangeError?.message || 'Failed to exchange code for token');
          }

          setMessage('Fetching Microsoft user profile...');

          // Fetch Microsoft user email
          const userInfoResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
            headers: { 'Authorization': `Bearer ${tokenData.accessToken}` }
          });

          if (!userInfoResponse.ok) {
            throw new Error('Failed to fetch Microsoft user profile');
          }

          const userInfo = await userInfoResponse.json();
          const userEmail = userInfo.mail || userInfo.userPrincipalName;

          if (!userEmail) {
            throw new Error('No email found in Microsoft profile');
          }

          setMessage('Verifying account...');

          // Call edge function to login existing user (won't create new users)
          // Note: We need to handle the response manually since FunctionsHttpError.context is the raw Response
          const invokeResult = await supabase.functions.invoke('login-existing-user', {
            body: {
              email: userEmail,
              graphToken: tokenData.accessToken
            }
          });
          
          const { data: sessionData, error: sessionError } = invokeResult;
          // Access the raw response for proper error parsing
          const rawResponse = (invokeResult as unknown as { response?: Response }).response;

          // Handle errors from Edge Function (including rate limits with 429 status)
          if (sessionError || sessionData?.error) {
            // Parse the actual error from the response body
            const parsed = await parseInvokeError(sessionError, rawResponse);
            
            // Also check sessionData for errors returned in 2xx responses
            const errorMessage = sessionData?.error || parsed.message;
            const blockedUntil = sessionData?.blockedUntil || parsed.blockedUntil;
            
            setStatus('error');
            
            // User-friendly error messages
            let friendlyMessage: string;
            if (errorMessage.includes('Too many login attempts') || parsed.status === 429) {
              friendlyMessage = `🚫 Too Many Login Attempts\n\n${formatRateLimitMessage(blockedUntil)}`;
            } else {
              friendlyMessage = `Login failed: ${errorMessage}`;
            }
            
            setMessage(friendlyMessage);
            
            // Notify parent window with friendly error
            if (window.opener) {
              window.opener.postMessage({
                type: 'microsoft-auth-error',
                error: friendlyMessage,
                blockedUntil,
                source: 'microsoft-graph-auth'
              }, window.location.origin);
            } else if ((window as any).Office?.context?.ui?.messageParent) {
              try {
                (window as any).Office.context.ui.messageParent(
                  JSON.stringify({
                    type: 'auth-error',
                    error: friendlyMessage,
                    blockedUntil
                  })
                );
              } catch (e) {
                // Silently fail
              }
            }
            
            setTimeout(() => window.close(), 6000);
            return;
          }

          if (!sessionData?.session) {
            throw new Error('Failed to create session');
          }

          // Set the session
          await supabase.auth.setSession({
            access_token: sessionData.session.access_token,
            refresh_token: sessionData.session.refresh_token
          });

          const userId = sessionData.session.user.id;
          
          // Store Graph token server-side via Edge Function
          const { error: storeError } = await supabase.functions.invoke('store-graph-token', {
            body: {
              graphToken: tokenData.accessToken,
              expiresInSeconds: 3600,
            },
          });
          
          // === PRIORITIZE Office messageParent for OWA/Edge ===
          // Wait for Office.js to be ready before calling messageParent
          const authMessage = JSON.stringify({
            type: 'auth-success',
            session: {
              access_token: sessionData.session.access_token,
              refresh_token: sessionData.session.refresh_token,
              expires_at: sessionData.session.expires_at,
              expires_in: sessionData.session.expires_in,
              user: sessionData.session.user,
            },
            graphToken: tokenData.accessToken,
          });

          // Helper: attempt messageParent with Office.onReady() wait
          const sendViaOffice = async (): Promise<boolean> => {
            const OfficeObj = (window as any).Office;
            if (!OfficeObj) return false;
            
            try {
              // Wait for Office.js to be fully initialized
              await new Promise<void>((resolve, reject) => {
                const timeout = setTimeout(() => reject(new Error('Office.onReady timeout')), 5000);
                OfficeObj.onReady(() => {
                  clearTimeout(timeout);
                  resolve();
                });
              });
              
              if (OfficeObj.context?.ui?.messageParent) {
                OfficeObj.context.ui.messageParent(authMessage);
                return true;
              }
            } catch (e) {
              // Office.onReady timed out or messageParent failed
            }
            return false;
          };

          const officeSent = await sendViaOffice();
          
          if (officeSent) {
            setStatus('success');
            setMessage('✅ Login successful! You can close this window.');
          }
          // Fallback to window.opener (popup flow, not OWA)
          else if (window.opener) {
            window.opener.postMessage({
              type: 'microsoft-auth-success',
              graphToken: tokenData.accessToken,
              session: {
                access_token: sessionData.session.access_token,
                refresh_token: sessionData.session.refresh_token,
                expires_at: sessionData.session.expires_at,
                expires_in: sessionData.session.expires_in,
                user: sessionData.session.user
              }
            }, window.location.origin);
            
            setTimeout(() => {
              window.close();
            }, 2000);
            
            setStatus('success');
            setMessage('✅ Login successful! Redirecting...');
          }
          // No communication channel available - direct redirect
          else {
            const redirectPath = sessionStorage.getItem('auth_redirect') || '/settings';
            sessionStorage.removeItem('auth_redirect');
            setTimeout(() => {
              window.location.href = redirectPath;
            }, 1000);
            
            setStatus('success');
            setMessage('✅ Login successful! Redirecting...');
          }
          return;
        }

        // No token or code received
        window.opener?.postMessage({
          status: 'error',
          error: 'No access token or authorization code received',
          source: 'microsoft-graph-auth'
        }, window.location.origin);
        
        setStatus('error');
        setMessage('No access token or authorization code received');
        setTimeout(() => window.close(), 2000);
        
      } catch (err) {
        setStatus('error');
        
        // User-friendly error mapping
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        
        if (errorMessage.includes('PKCE') || errorMessage.includes('verifier')) {
          setMessage('Session expired. Please start the login process again.');
        } else if (errorMessage.includes('Token exchange failed')) {
          setMessage('Microsoft authentication failed. Please try again.');
        } else if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
          setMessage('Network error. Please check your connection and try again.');
        } else if (errorMessage.includes('profile') || errorMessage.includes('Microsoft user')) {
          setMessage('Could not retrieve your Microsoft profile. Please try again.');
        } else if (errorMessage.includes('session') || errorMessage.includes('Session')) {
          setMessage('Session error. Please start the login process again.');
        } else if (errorMessage.includes('FunctionsHttpError') || errorMessage.includes('Edge Function')) {
          setMessage('Server error. Please try again in a moment.');
        } else {
          setMessage('An unexpected error occurred. Please try again.');
        }
        
        window.opener?.postMessage({
          status: 'error',
          error: err instanceof Error ? err.message : 'Unknown error',
          source: 'microsoft-graph-auth'
        }, window.location.origin);
        
        setTimeout(() => window.close(), 2000);
      }
    };

    handleCallback();
  }, []);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center space-y-4 p-8">
        {status === 'processing' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary" />
            <p className="text-sm text-muted-foreground">{message}</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-green-600" />
            <p className="text-sm text-green-600 font-medium">{message}</p>
          </>
        )}
        
        {status === 'error' && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-destructive" />
            <p className="text-sm text-destructive font-medium">{message}</p>
          </>
        )}
      </div>
    </div>
  );
};
